/** @jsx jsx */

import React, {
  Fragment as _,
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import closeFullscreen from 'assets/CloseFullscreenEmbed.svg';
import expand from 'assets/expand.svg';

export const ImageZoomContext = createContext({ overideStyles: {} });

const OPEN = 'open';
const CLOSE = 'close';

const ImageZoom = props => {
  const { src, description, noDescription, imageZoomBackgroundColor } = props;

  const [zoom, setZoom] = useState<undefined | 'open' | 'close'>(undefined);
  const { overideStyles } = useContext(ImageZoomContext);

  const imageZoom = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (zoom === undefined) {
      return;
    }

    document.body.style.overflowY = zoom === OPEN ? 'hidden' : 'auto';

    if (zoom === 'close') {
      requestAnimationFrame(() => {
        imageZoom?.current?.focus();
        setZoom(undefined);
      });
    }
  }, [zoom]);

  if (window.dev()) {
    // return null;
  }

  return (
    <_>
      <div
        css={css({
          position: 'absolute',
          display: 'flex',
          justifyContent: 'flex-end' || 'center',
          alignItems: 'center',
          bottom: '5px',
          width: '100%',
          height: 40,
          padding: '0 5px',

          //
          ...overideStyles,
        })}
      >
        <button
          ref={imageZoom}
          className="zoom-image"
          css={css({
            all: 'unset',
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            cursor: 'pointer',

            background: 'rgba(0, 0, 0, 0.1)',

            ':hover': {
              opacity: 0.5,
            },

            '*': {
              pointerEvents: 'none',
            },

            ':focus': {
              boxShadow: `0 0 0 1px ${'#000'}`,
              // TODO potentially add some color from somewhere
              // aHiliteColor.current
            },

            // color: '#000',
          })}
          onClick={e => {
            e.stopPropagation();
            setZoom(OPEN);
          }}
          aria-label="expand image"
        >
          <img
            src={expand}
            css={css({
              position: 'relative',
              // top: 1,
              width: 15,
              height: 15,
            })}
          />
        </button>
      </div>

      {zoom &&
        createPortal(
          <ImageZoomed
            setZoom={setZoom}
            src={src}
            description={description}
            noDescription={noDescription}
            imageZoomBackgroundColor={imageZoomBackgroundColor}
          />,
          document.querySelector('#image-zoom-container') as HTMLDivElement
        )}
    </_>
  );
};

const ImageZoomed = props => {
  const {
    setZoom,
    src,
    description,
    noDescription,
    imageZoomBackgroundColor,
  } = props;

  const imageZoomed = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (window.dev()) {
      return;
    }

    if (!closeBtn.current) {
      return;
    }

    // give focus when opened
    closeBtn.current.focus();
  }, []);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    if (!imageZoomed.current) {
      return;
    }

    const onKeyDown = e => {
      switch (e.key) {
        case 'Tab': {
          // console.log('Tab captured in imageZoom', e.key);
          e.preventDefault();
          closeBtn?.current?.focus();
          return;
        }

        case 'Escape': {
          setZoom(CLOSE);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <div
      ref={imageZoomed}
      css={css({
        all: 'unset',
        position: 'fixed',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.8)',
        zIndex: 2147483647,
      })}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      <div
        className="fader_-5s"
        style={{
          padding: 50,
          borderRadius: 10,
          background: imageZoomBackgroundColor,
          maxWidth: '90%',
          overflow: 'hidden',
        }}
      >
        <img src={src} alt={description || noDescription} />
      </div>

      <button
        ref={closeBtn}
        css={css({
          all: 'unset',
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          cursor: 'pointer',

          ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },

          ':focus': {
            boxShadow: '0 0 0 1px #fff',
          },

          transition: 'box-shadow 0.2s',
        })}
        aria-label="close expanded image view"
        onClick={e => {
          e.stopPropagation();
          setZoom(CLOSE);
        }}
      >
        <img src={closeFullscreen} />
      </button>
    </div>
  );
};

export { ImageZoom };
